import {CanonicalizeUnicodeLocaleId} from '#packages/intl-getcanonicallocales/canonicalizer.js'
import {emitUnicodeLocaleId} from '#packages/intl-getcanonicallocales/emitter.js'
import {parseUnicodeLocaleId} from '#packages/intl-getcanonicallocales/parser.js'

/**
 * Check if value is an Intl.Locale object by checking for [[InitializedLocale]] internal slot
 * We detect this by checking if it's an Intl.Locale instance
 *
 * Per ECMA-402 #sec-canonicalizelocalelist step 7.c:
 * "If Type(kValue) is Object and kValue has an [[InitializedLocale]] internal slot, then
 *  Let tag be kValue.[[Locale]]"
 *
 * https://tc39.es/ecma402/#sec-canonicalizelocalelist
 */
function isLocaleObject(value: any): value is Intl.Locale {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof value.toString === 'function' &&
    typeof value.baseName === 'string' &&
    typeof value.language === 'string'
  )
}

/**
 * https://tc39.es/ecma402/#sec-canonicalizelocalelist
 * @param locales
 */
function CanonicalizeLocaleList(
  locales?:
    | string[]
    | string
    | Intl.Locale
    | Intl.Locale[]
    | ArrayLike<string | Intl.Locale>
): string[] {
  // Step 1-2: If locales is undefined, return empty list
  if (locales === undefined) {
    return []
  }

  const seen: string[] = []

  // Step 3-4: Handle string or Locale object by wrapping in array
  // Per spec: "If Type(locales) is String or Type(locales) is Object and locales has an
  // [[InitializedLocale]] internal slot, then Let O be CreateArrayFromList(« locales »)"
  if (typeof locales === 'string' || isLocaleObject(locales)) {
    locales = [locales as string | Intl.Locale]
  }

  // Step 5-6: Convert to object and get length for array-like objects
  // Per spec: "Let O be ? ToObject(locales)" and "Let len be ? ToLength(? Get(O, "length"))"
  const O = Object(locales)
  const len = typeof O.length === 'number' ? O.length : 0

  // Step 7: Iterate through elements
  for (let k = 0; k < len; k++) {
    // Check if property exists
    if (!(k in O)) {
      continue
    }

    const kValue = O[k]

    // Step 7c-d: Extract locale string
    let tag: string
    if (typeof kValue === 'string') {
      tag = kValue
    } else if (isLocaleObject(kValue)) {
      // For Intl.Locale objects, use toString() which returns the canonicalized locale
      tag = kValue.toString()
    } else {
      throw new TypeError(
        `Invalid locale type: expected string or Intl.Locale, got ${typeof kValue}`
      )
    }

    // Step 7e-g: Validate and canonicalize
    const canonicalizedTag = emitUnicodeLocaleId(
      CanonicalizeUnicodeLocaleId(parseUnicodeLocaleId(tag))
    )

    // Step 7h: Deduplicate
    if (seen.indexOf(canonicalizedTag) < 0) {
      seen.push(canonicalizedTag)
    }
  }

  return seen
}

export function getCanonicalLocales(
  locales?:
    | string[]
    | string
    | Intl.Locale
    | Intl.Locale[]
    | ArrayLike<string | Intl.Locale>
): string[] {
  return CanonicalizeLocaleList(locales)
}

export * from '#packages/intl-getcanonicallocales/emitter.js'
export {
  isStructurallyValidLanguageTag,
  isUnicodeLanguageSubtag,
  isUnicodeRegionSubtag,
  isUnicodeScriptSubtag,
  parseUnicodeLanguageId,
  parseUnicodeLocaleId,
} from '#packages/intl-getcanonicallocales/parser.js'
export * from '#packages/intl-getcanonicallocales/types.js'

export * from '@formatjs_generated/cldr.core/likelySubtags.js'
